<template>
  <b-card no-body class="vessels">
    <template #header>
      <div class="header">
        <div class="header__title">
          {{ title }}: <strong>{{ vessels.length }}</strong>
        </div>
        <div class="header__controls">
          <buttons-select :choices="mode.choices" v-model="mode.value" />
          <slot name="header-controls-extra">
            <!-- export csv for example -->
          </slot>
        </div>
      </div>
    </template>
    <b-card-body :class="cardBodyClass">
      <div v-if="isDataEmpty" class="text-center">
        No data available
      </div>

      <div v-else class="content-container">
        <!-- table tab -->
        <template v-if="mode.value === 'table'">
          <c-table
            class="content-container__table"
            :table-data="tableRows"
            :fields="tableColumns"
            :sorting="true"
            :striped="true"
            :bordered="false"
            :small="false"
            :perPage="13"
            :currentPageExternal="null"
            @page-changed="() => {}"
            caption=""
          >
          </c-table>
        </template>

        <!-- map tab -->
        <template v-if="mode.value === 'map'">
          <base-map
            :map-center="map.center"
            :map-zoom="map.zoom"
            :map-bounds="mapBounds"
            :vessels="vesselsFormatted"
          >
            <template #control-bottom-left>
              <div class="vessels-info">
                <div class="vessels-stat map-overlay">
                  <div class="vessels-stat__count">
                    Total vessels: {{ tableRows.length }}
                  </div>

                  <div class="vessels-stat__notes">
                    Only active vessels with positions within last 30 days
                    included.
                  </div>
                </div>

                <div class="vessels-legend map-overlay">
                  <p
                    v-for="(item, index) in map.legend"
                    :key="`legend-item-${index}`"
                    class="vessels-legend__item"
                  >
                    <span class="vessels-legend__icon-container">
                      <img class="icon" :src="item.icon" :alt="item.title" />
                    </span>
                    <span class="vessels-legend__text">{{ item.title }}</span>
                  </p>
                </div>
              </div>
            </template>
          </base-map>
        </template>
      </div>
    </b-card-body>
  </b-card>
</template>

<script>
import getNested from "lodash-es/get";

import ButtonsSelect from "@/components/widgets/ButtonsSelect";
import BaseMap from "@/base_components/BaseMap";
import CTable from "@/components/Table";
import { getCoordinateBounds } from "@/shared/coordinate-utils";

export default {
  name: "congestion-vessels",

  components: {
    CTable,
    BaseMap,
    ButtonsSelect
  },

  props: {
    title: {
      type: String,
      default: "No title specified"
    },

    vessels: {
      type: Array,
      default: () => []
    }
  },

  data() {
    return {
      mode: {
        value: "table",
        choices: [
          { value: "table", text: "Show as Table" },
          { value: "map", text: "Show on Map" }
        ]
      },

      map: {
        zoom: 4,
        defaultVesselZoom: 8,
        center: [47.41322, -1.219482],
        legend: [
          {
            title: "Laden (underway)",
            icon: require("@/assets/icons/vessels/vessel-dark-arrow.svg")
          },
          {
            title: "Ballast (underway)",
            icon: require("@/assets/icons/vessels/vessel-light-arrow.svg")
          },
          {
            title: "Laden (still)",
            icon: require("@/assets/icons/vessels/vessel-dark-circle.svg")
          },
          {
            title: "Ballast (still)",
            icon: require("@/assets/icons/vessels/vessel-light-circle.svg")
          }
        ]
      }
    };
  },

  methods: {
    getLadenStatus(vessel) {
      const statusMap = {
        L: "Laden",
        B: "Ballast",
        na: "na"
      };
      const ladenStatus = getNested(vessel, "ladenStatus", "na");
      return statusMap[ladenStatus] ? statusMap[ladenStatus] : statusMap["na"];
    }
  },

  computed: {
    isDataEmpty() {
      return false;
    },

    cardBodyClass() {
      return this.mode.value === "map" ? "p-0" : "";
    },

    tableColumns() {
      return [
        { key: "imo", sortable: true },
        { key: "vesselName", sortable: true },
        { key: "subSegment", sortable: true },
        { key: "currentCountry", sortable: true },
        { key: "currentPortName", sortable: true },
        { key: "arrivedAt", sortable: true },
        { key: "waitingTimeDays", sortable: true },
        { key: "commodity", sortable: true },
        { key: "commodityGroup", sortable: true },
        { key: "lastLoadCountry", sortable: true },
        { key: "lastLoadPortName", sortable: true },
        { key: "dwt", sortable: true },
        { key: "ladenStatus", sortable: true }
      ];
    },

    tableRows() {
      return this.vessels.map(item => {
        return {
          ...item,
          waitingTimeDays: getNested(item, "waitingTimeDays", 0).toFixed(2),
          ladenStatus: this.getLadenStatus(item)
        };
      });
    },

    vesselsFormatted() {
      return this.vessels.map(item => {
        return {
          imo: item.imo,
          position: {
            speed: item.speed,
            course: item.course
          },
          state: {
            ladenStatus: this.getLadenStatus(item)
          },
          geometry: {
            coordinates: [item.lng, item.lat]
          },
          popupData: {
            IMO: item.imo,
            "Vessel name": item.vesselName,
            Commodity: item.commodity,
            "Commodity group": item.commodityGroup,
            Segment: item.segment,
            Subsegment: item.subSegment,
            "Current country": item.currentCountry,
            "Current country code": item.currentCountryCode,
            "Current port name": item.currentPortName,
            DWT: item.dwt,
            "Laden status": this.getLadenStatus(item),
            Speed: item.speed,
            "Waiting time days": getNested(item, "waitingTimeDays", 0).toFixed(
              2
            ),
            "Arrived at": item.arrivedAt
          }
        };
      });
    },

    mapBounds() {
      // adjust map bounds according to new vessels received
      return getCoordinateBounds(this.vessels);
    }
  },

  watch: {
    // ...
  }
};
</script>

<style scoped>
.vessels >>> .card-header {
  padding-right: 5px;
}

.header__controls >>> .choice-buttons {
  margin: 0;
}
</style>

<style scoped lang="scss">
.vessels-info {
  max-width: 170px;

  // bottom left vessels count stats
  .vessels-stat {
    padding: 5px;
    border-radius: 5px;

    &__count {
      font-size: 14px;
    }

    &__notes {
      font-size: 11px;
    }
  }

  // bottom left legend
  .vessels-legend {
    margin-top: 10px;
    padding: 5px 10px 5px 0;
    border-radius: 5px;

    &__item {
      margin: 0;
      display: flex;
      justify-content: flex-start;
      align-items: center;
    }

    &__item:not(:last-child) {
      margin-bottom: 5px;
    }

    &__icon-container {
      width: 45px;
      text-align: center;

      img.icon {
        margin: -5px 0;
        transform: rotate(90deg);
        max-height: 25px;
        max-width: 12px;
      }
    }
  }
}

.vessels {
  .header {
    display: flex;
    justify-content: space-between;
    align-items: center;

    &__controls {
      display: flex;
    }
  }

  .content-container {
    height: 100%;

    &__table {
      height: 100%;
    }
  }
}
</style>
